import { CheckCircle, GripVertical, Loader2, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { ConfirmDialog } from './ui/alert-dialog-helper';

type Img = { id?: string; url?: string | null; position: number };

export default function ReorderableGrid({ images, templateId, onReordered }: { images: Img[]; templateId: string; onReordered: () => void }) {
  const [local, setLocal] = useState<Img[]>([...images].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)));
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<Img | null>(null);
  const [orderChanged, setOrderChanged] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget.innerHTML);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e: React.DragEvent, index: number) => {
    if (draggedIndex === null || draggedIndex === index) return;

    const next = [...local];
    const draggedItem = next[draggedIndex];
    next.splice(draggedIndex, 1);
    next.splice(index, 0, draggedItem);
    next.forEach((img, idx) => (img.position = idx));

    setLocal(next);
    setDraggedIndex(index);
    setOrderChanged(true);
  };

  const handleDragEnd = async () => {
    setDraggedIndex(null);

    // Auto-save if order changed
    if (orderChanged) {
      await persist();
      setOrderChanged(false);
    }
  };

  const persist = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const body = { order: local.filter(i => i.id).map(i => ({ id: i.id!, position: i.position })) };
      const r = await fetch(`/api/asset-templates/${templateId}/images/reorder`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) });
      if (!r.ok) throw new Error('Failed to reorder');
      onReordered();
    } catch (e) {
      window.alert('Failed to save order');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (img: Img) => {
    setImageToDelete(img);
    setDeleteDialogOpen(true);
  };

  const remove = async () => {
    if (!imageToDelete?.id) return;

    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`/api/asset-templates/images/${imageToDelete.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('Failed to delete');
      setDeleteDialogOpen(false);
      setImageToDelete(null);
      onReordered();
    } catch (e) {
      window.alert('Failed to delete image');
      setDeleteDialogOpen(false);
    }
  };

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {local.map((img, idx) => (
          <div
            key={img.id || idx}
            draggable
            onDragStart={(e) => handleDragStart(e, idx)}
            onDragOver={handleDragOver}
            onDragEnter={(e) => handleDragEnter(e, idx)}
            onDragEnd={handleDragEnd}
            className={`border rounded overflow-hidden cursor-move transition-all ${
              draggedIndex === idx ? 'opacity-50 scale-95' : 'hover:shadow-md'
            }`}
          >
            {img.url ? (
              <div className="w-full h-32 bg-gray-50 flex items-center justify-center">
                <img src={img.url} alt={`web-${idx + 1}`} className="max-w-full max-h-full object-contain pointer-events-none" />
              </div>
            ) : (
              <div className="h-32 flex items-center justify-center text-xs text-muted-foreground">no preview</div>
            )}
            <div className="flex items-center justify-between p-2 text-xs bg-white">
              <div className="flex items-center gap-1">
                <GripVertical className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">#{idx + 1}</span>
              </div>
              {img.id && (
                <button
                  className="p-1 hover:bg-gray-100 rounded text-red-600 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(img);
                  }}
                  title="Delete image"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Drag and drop images to reorder • Changes save automatically
        </p>
        {saving && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Saving...
          </div>
        )}
        {!saving && orderChanged === false && local.length > 0 && (
          <div className="flex items-center gap-2 text-sm text-green-600">
            <CheckCircle className="h-4 w-4" />
            Saved
          </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Image"
        description="Are you sure you want to delete this image? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={remove}
        variant="destructive"
      />
    </div>
  );
}


